import {
  Component,
  ContentChild,
  inject,
  Input,
  OnInit,
  TemplateRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Mold } from '../../../../api/elite/types/mold';
import { MoldRevisionItem } from '../../models/item';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import {
  MAT_DIALOG_DATA,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatOptionModule } from '@angular/material/core';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatStepperModule } from '@angular/material/stepper';
import { NgxMatSelectSearchModule } from 'ngx-mat-select-search';
import { PaginatorModule } from 'primeng/paginator';
import { MoldFormApiService } from '../../api/form.service';
import { MoldDialogCreateMoldService, MoldDialogCreateNamesForm } from '../create-mold/create-mold.service';
import { MoldDialogFormService } from './form.service';
import { MoldDialogCreateMoldComponent } from '../create-mold/create-mold.component';
import { Machine } from '../../../../api/elite/types/machine';
import { LayoutService } from '../../../../core/services/layout.service';
import { combineLatest } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import { MoldAuthService } from '../../services/auth.service';
import { MoldCreateFormButtonsDirective } from './form.directive';
import { ChipsInputComponent } from '../../../../shared/components/chips-input/chips-input.component';
import { MatAutocompleteModule } from '@angular/material/autocomplete';

/**
 * TODO deberemos de separar la logica de los baseplates dentro de injection molding nada mas.
 */
export type MoldFormDialogTypes = {
  data: {
    image: File | null;
    molds: Mold[];
    moldRevision?: MoldRevisionItem;
    selectMoldType: boolean;
    type: 'create' | 'edit';
    selectBasePlates: boolean;
    selectItemCavities: boolean;
    machines: Machine[];
    department: string;
    inputsToEnable?: MoldDialogCreateNamesForm[];
    enabledTabs?: string[]
  };
  return: {
    suffix: string;
    assembly: string;
    department: string;
    image: File | null;
    version: number;
    moldData: {
      name: string;
      cyclesToObsolete: number;
      cyclesForRepair: number;
      details: {
        cavities: number;
        typeId?: number;
        serialized: boolean;
      };
    };
    machines: number[];
    basePlates: number[];
    moldId?: number;
    locationId: number;
    itemCavities?: {
      assemblyItemId: number;
      cavities: number;
    }[];
  };
};

@Component({
  selector: 'app-mold-dialog-form',
  standalone: true,
  imports: [
    CommonModule,
    MatButtonModule,
    MatDialogModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatOptionModule,
    MatSelectModule,
    MatSlideToggleModule,
    MatStepperModule,
    NgxMatSelectSearchModule,
    PaginatorModule,
    ReactiveFormsModule,
    ChipsInputComponent,
    MoldDialogCreateMoldComponent,
    MatAutocompleteModule,
  ],
  templateUrl: './form.component.html',
  styleUrls: ['./form.component.sass'],
  providers: [MoldDialogCreateMoldService, MoldDialogFormService],
})
export class MoldFormDialogComponent implements OnInit {
  private readonly data: MoldFormDialogTypes['data'] = inject(MAT_DIALOG_DATA);
  private readonly dialogRef = inject(MatDialogRef);

  private readonly layoutService = inject(LayoutService);

  private readonly formApiRouter = inject(MoldFormApiService);
  private readonly moldFormService = inject(MoldDialogCreateMoldService);
  private readonly service = inject(MoldDialogFormService);
  private readonly authService = inject(MoldAuthService);

  @Input() extraSteps: {
    template: TemplateRef<any>;
    label: string;
  }[] = [];

  get moldForm(): FormGroup {
    return this.moldFormService.form;
  }

  suffixForm = new FormGroup({
    suffix: new FormControl<string>(
      this.data?.moldRevision?.suffix,
      Validators.required
    ),
    location: new FormControl<number>(
      this.data.moldRevision?.locationId,
      Validators.required
    ),
    searchLocation: new FormControl<string>(null),
  });

  locationOptions$ = combineLatest([
    this.service.locations$,
    this.suffixForm.get('searchLocation').valueChanges.pipe(startWith('')),
  ]).pipe(
    map(([locations, search]) =>
      locations.filter((l) =>
        l.title.toLowerCase().includes(search?.toLowerCase())
      )
    )
  );
  // assemblyItemsForm: FormGroup<FormAssemblyItem> = new FormGroup({});
  // assemblyItemList = [];

  molds = this.data.molds;

  get dialogTitle(): string {
    return this.data.type === 'create' ? 'Crear molde' : 'Editar molde';
  }

  get selectBasePlates(): boolean {
    return this.data.selectBasePlates;
  }

  get selectBasic(): boolean {
    if (!this.data.enabledTabs?.length){ return true; }
    return this.data.enabledTabs.includes('basic');
  }

  get selectMachines(): boolean {
    if (!this.data.enabledTabs?.length) {
      return true;
    }
    return this.data.enabledTabs.includes('machines');
  }

  get selectItemCavities(): boolean {
    return this.data.selectItemCavities;
  }

  get basePlates(): { id: number; title: string }[] {
    return this.service.basePlates;
  }

  selectedBasePlates: { id: number; title: string }[] = [];

  showStepSelectionBasePlates = false;

  showFormMachines = false;
  selectedMachines: Machine[] = [];

  get machines(): Machine[] {
    return this.data.machines;
  }

  @ContentChild(MoldCreateFormButtonsDirective, { read: TemplateRef })
  templateButtons: TemplateRef<any> | undefined = undefined;

  constructor() {}

  ngOnInit(): void {
    this.moldFormService.chargeDepartmentData(
      this.data.department,
      this.data.type,
      this.data.moldRevision
    );
    this.setUpForm();

    if (this.data.selectMoldType) {
      this.selectMoldType();
    }

    if (this.data.moldRevision) {
      this.moldFormService.setMoldRevision(
        this.data.moldRevision,
        this.data.type,
        this.data.inputsToEnable,
      );
      this.selectedMachines = this.data.moldRevision.mold.machines;
    }
    if (this.data.machines) {
      this.showFormMachines = true;
    }
    this.service.setMoldLocations(this.data.moldRevision?.location);
  }

  private selectMoldType(): void {
    this.layoutService.setLoading(true);
    this.moldFormService.showStepSelectionBasePlates$.subscribe((val) => {
      this.showStepSelectionBasePlates = val;
    });
    this.formApiRouter
      .moldTypesByDepartment(this.data.department)
      .subscribe((types) => {
        this.moldFormService.moldTypes = types;
        this.layoutService.setLoading(false);
        if (this.data.moldRevision?.mold?.details.type.title === 'Mold') {
          this.showStepSelectionBasePlates = true;
        }
      });
  }

  setUpForm(): void {
    this.service.selectedBasePlates$.subscribe((val) => {
      this.selectedBasePlates = val;
    });

    this.suffixForm.get('suffix').valueChanges.subscribe((val) => {
      const sanitized = val?.toUpperCase().trim();
      if (val !== sanitized) {
        this.suffixForm.get('suffix').setValue(sanitized);
      }
    });
    if (!this.authService.editLocation() && this.data.type === 'edit') {
      this.suffixForm.get('location').disable();
    }
  }

  resultData(): MoldFormDialogTypes['return'] {
    const title = this.moldForm.get('name').value.trim();

    const isEdit = this.data.type === 'edit';

    const mold = this.service.selectedMoldValue;

    const version = mold?.details?.resourceFiles?.storageName
      ? parseInt(mold.details.resourceFiles.storageName, 10) + 1
      : 1;

    return {
      suffix: this.suffixForm.get('suffix').value,
      assembly: this.moldForm.get('assembly').value,
      image: this.moldForm.get('image').value,
      version,
      department: this.data.department,
      moldData: {
        name: title,
        cyclesForRepair: this.moldForm.get('cyclesToRepair').value,
        cyclesToObsolete: this.moldForm.get('cyclesToObsolete').value,
        details: {
          cavities: this.moldForm.get('cavities').value,
          typeId: this.moldForm.get('moldType').value,
          serialized: this.moldForm.get('serialized').value,
        },
      },

      moldId: isEdit ? undefined : mold?.id,
      locationId: this.suffixForm.get('location').value,
      machines: this.selectedMachines.map((m) => m.id),
      basePlates: this.selectedBasePlates.map((bp) => bp.id),
    };
  }

  closeDialog(): void {
    this.dialogRef.close(this.resultData());
  }
}
